
import { alpha, Box, Button, IconButton, useMediaQuery } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React from "react";
import NoteEditor from "../new-editor/NoteEditor";
import { XIcon } from "lucide-react";
import clsx from "clsx";
import { clipTrailingNodes } from "../editor/normalizers/withSchema";
import { ElementType } from "../editor/types/elements";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        padding: theme.spacing(2, 2, 0, 2),

        [theme.breakpoints.up("lg")]: {
            maxWidth: theme.drawer.width
        },
    },

    '@keyframes slideIn': {
        from: {
            transform: 'translateX(-100%)',
            opacity: 0,
        },
        to: {
            transform: 'translateX(0)',
            opacity: 1,
        },
    },

    slideIn: {
        animation: `$slideIn 400ms cubic-bezier(0.4, 0, 0.2, 1)`,
    },

    container: {
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing(2),
        backgroundColor: alpha(theme.palette.border.tertiary, 0.5),
        borderRadius: theme.spacing(1),
        borderColor: theme.palette.border.secondary,
        borderWidth: 2,
        maxHeight: theme.spacing(24),
        overflowX: "hidden",

        [theme.breakpoints.up("lg")]: {
            maxHeight: theme.spacing(30),
        }
    },

    closeButton: {
        zIndex: 2,
        position: "absolute",
        top: theme.spacing(0.5),
        right: theme.spacing(0.5),
        color: theme.palette.text.secondary,
    },

    contentWrapper: {
        marginTop: theme.spacing(0.5),
        width: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarWidth: "none",
    },

    content: {
        marginTop: theme.spacing(-1),
        zIndex: 1,
        width: "100%",
    },

    title: {
        display: "flex",
        gap: theme.spacing(1),
        margin: theme.spacing(0.5, 1),
        fontSize: 16,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        fontFamily: theme.typography.fontFamily,
    },

    text: {
        width: "100%",
        fontSize: 14,
        fontWeight: 400,
        color: theme.palette.text.secondary,
        fontFamily: theme.typography.fontFamily,
        marginTop: theme.spacing(1),
        textAlign: "center",
        maxHeight: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarWidth: "none",
    },

    line: {
        wordBreak: "break-word",
    },

    button: {
        marginTop: theme.spacing(2),
        width: "100%",
        fontFamily: theme.typography.fontFamily,
        fontWeight: 700,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        "&:hover": {
            backgroundColor: theme.palette.primary.light
        },
    }
}))

interface DrawerAnouncementProps {
    content?: any[];
    buttonText?: string;
    link?: string;
    closable?: boolean;
    animation?: boolean;
}

export function DrawerAnnouncement(props: DrawerAnouncementProps) {
    const { content, buttonText, link, closable = true, animation = true } = props;
    const s = useStyles();
    const smUp = useMediaQuery((theme) => theme.breakpoints.up("sm"));
    const isLandscape = useMediaQuery("(orientation: landscape)");
    const downMd = useMediaQuery((theme) => theme.breakpoints.down("md"));
    const [open, setOpen] = React.useState(true);

    const handleClick = () => {
        if (link) {
            window.open(link, "_blank");
        }
    }

    const hasContent = content?.filter((node: any) => {
        if (node.type !== ElementType.PARAGRAPH && node.type !== "p") {
            return true;
        } else {
            return node.children.some((child: any) => {
                return child.text && child.text.trim().length > 0;
            });
        }
    })?.length > 0;

    return (hasContent || buttonText) && (
        <Box className={clsx(s.root, animation && s.slideIn)}>
            { !(downMd && isLandscape) && open && <Box className={s.container} onClick={() => !smUp && handleClick()}>
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation();
                        closable && setOpen(false)
                    }}
                    className={s.closeButton}
                    color="secondary"
                    size="small"
                >
                    <XIcon size={15} />
                </IconButton>

                {content?.length && 
                <Box className={s.contentWrapper}>
                    <NoteEditor 
                        readOnly 
                        value={content} 
                        onChange={() => {}} 
                        className={s.content}
                        key={JSON.stringify(content)}
                    />
                </Box>}

                {buttonText && smUp && 
                    <Button variant="contained" className={s.button} onClick={handleClick}>
                        {buttonText}
                    </Button>
                }
            </Box>}
        </Box>
    )
}